import React, { useState, useEffect } from 'react';
import OnboardingUpload from './OnboardingUpload';
import OnboardingReview from './OnboardingReview';
import OnboardingQuickForm from './OnboardingQuickForm';
import OnboardingExcelUpload from './OnboardingExcelUpload';
import { useOnboardingMixpanelTrack } from './hooks/useOnboardingMixpanelTrack';
import { useTransporterData } from '../../hooks/useTransporterData';

const SCREENS = {
    WELCOME: 'welcome',
    UPLOAD: 'upload',
    REVIEW: 'review',
    QUICKFORM: 'quickform',
    EXCEL: 'excel',
};

/**
 * OnboardingGate
 * Top-level orchestrator for the new-transporter onboarding flow.
 * Rendered inside Wizard.js when transporter_name is absent.
 *
 * @param {object}   transporterData - raw profile object fetched from API
 * @param {object}   history         - react-router history
 * @param {function} refetch         - re-fetches transporter data after save
 */
const OnboardingGate = ({ transporterData, history, refetch }) => {
    const [screen, setScreen] = useState(SCREENS.QUICKFORM);
    const [extractedFields, setExtractedFields] = useState({});

    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();
    const { data: transporterInfo } = useTransporterData();

    // Track initial landing on QuickForm
    useEffect(() => {
        trackOnboardingEvent(
            'onboarding quick form',
            'Onboarding started — Quick Form',
            { transporter_id: transporterInfo?.transporter_id }
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // After either path saves successfully → refresh data and navigate to normal app
    const handleSuccess = () => {
        trackOnboardingEvent(
            'onboarding complete',
            'Onboarding flow completed successfully',
            { transporter_id: transporterInfo?.transporter_id }
        );
        if (refetch) refetch();
        history.push('/generalInfo');
    };

    switch (screen) {
        case SCREENS.UPLOAD:
            return (
                <OnboardingUpload
                    onExtracted={fields => {
                        setExtractedFields(fields);
                        setScreen(SCREENS.REVIEW);
                    }}
                    onSwitchPath={() => {
                        trackOnboardingEvent(
                            'onboarding upload',
                            'User switched back to Quick Form from Upload',
                            { transporter_id: transporterInfo?.transporter_id }
                        );
                        setScreen(SCREENS.QUICKFORM);
                    }}
                />
            );

        case SCREENS.REVIEW:
            return (
                <OnboardingReview
                    fields={extractedFields}
                    transporterData={transporterData}
                    onSuccess={handleSuccess}
                    onBack={() => setScreen(SCREENS.UPLOAD)}
                    onSwitchPath={() => setScreen(SCREENS.QUICKFORM)}
                />
            );

        case SCREENS.EXCEL:
            return (
                <OnboardingExcelUpload
                    onSuccess={handleSuccess}
                    onSwitchPath={() => {
                        trackOnboardingEvent(
                            'onboarding excel',
                            'User switched back to Quick Form from Excel',
                            { transporter_id: transporterInfo?.transporter_id }
                        );
                        setScreen(SCREENS.QUICKFORM);
                    }}
                />
            );

        case SCREENS.QUICKFORM:
            return (
                <OnboardingQuickForm
                    transporterData={transporterData}
                    onSuccess={handleSuccess}
                    onSwitchToUpload={() => {
                        trackOnboardingEvent(
                            'onboarding upload',
                            'User switched to AI document upload path',
                            { transporter_id: transporterInfo?.transporter_id }
                        );
                        setScreen(SCREENS.UPLOAD);
                    }}
                    onSwitchToExcel={() => {
                        trackOnboardingEvent(
                            'onboarding excel',
                            'User switched to Excel template path',
                            { transporter_id: transporterInfo?.transporter_id }
                        );
                        setScreen(SCREENS.EXCEL);
                    }}
                />
            );

        default:
            return null;
    }
};

export default OnboardingGate;
